/**
 * Display formatting. Mirrors `utils/currency.py` and `utils/timefmt.py`.
 *
 * The number convention is the international one, confirmed with the operator:
 * **comma is the thousands separator, dot is the decimal point.** `1,234` and
 * `1.234` are different numbers. `he-IL` formats this way, which is why it is
 * used explicitly rather than relying on the browser's locale — a visitor with
 * a German locale would otherwise see `1.234,56` for the same value.
 */

const EM_DASH = '—'

export function fmtMoney(value: number | null | undefined, currency = 'USD'): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EM_DASH
  const symbol = currency === 'ILS' ? '₪' : '$'
  return `${symbol}${value.toLocaleString('he-IL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

export function fmtQty(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EM_DASH
  // Quantities are often whole; showing "12.00" adds noise to a dense grid.
  return value.toLocaleString('he-IL', { maximumFractionDigits: 2 })
}

export function fmtPct(value: number | null | undefined): string {
  if (!value) return EM_DASH
  return `${value.toLocaleString('he-IL', { maximumFractionDigits: 2 })}%`
}

/**
 * A stored UTC timestamp, rendered in the viewer's local time.
 *
 * The server stores UTC and says so; the browser is the only party that knows
 * the viewer's timezone. Rendering the raw string would show every timestamp
 * three hours behind for an Israeli user — the exact bug fixed on the desktop
 * side in `utils/timefmt.py`.
 */
export function fmtDateTime(stored: string | null | undefined): string {
  const parsed = parseStored(stored)
  if (!parsed) return EM_DASH
  return parsed.toLocaleString('he-IL', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  })
}

export function fmtDate(stored: string | null | undefined): string {
  const parsed = parseStored(stored)
  if (!parsed) return EM_DASH
  return parsed.toLocaleDateString('he-IL', {
    year: 'numeric', month: '2-digit', day: '2-digit',
  })
}

function parseStored(stored: string | null | undefined): Date | null {
  if (!stored) return null
  // "YYYY-MM-DD HH:MM:SS" is UTC but carries no zone marker, so it must be
  // told so explicitly — `new Date("2026-09-08 04:00:48")` is parsed as *local*
  // by every browser, which silently reintroduces the offset bug.
  const iso = stored.includes('T') ? stored : stored.replace(' ', 'T')
  const withZone = /[Zz]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`
  const date = new Date(withZone)
  return Number.isNaN(date.getTime()) ? null : date
}

export { EM_DASH }
